import { formatAddress } from '@medplum/core'
import type { Address } from '@medplum/fhirtypes'
import type { AddressValidationResult } from '@seen/request-types/addressLookup'
import { Button } from '@seen/ui'
import { Alert, AlertDescription, AlertTitle } from '@seen/ui/alert'
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconMapPinQuestion,
  IconWand,
} from '@tabler/icons-react'
import type { JSX } from 'react'
import { describeAddressComponents } from '#src/components/address/addressFields.ts'
import { Spinner } from '#src/components/core/Loader.tsx'

export interface AddressValidationNoticeProps {
  result?: AddressValidationResult
  isChecking?: boolean
  isError?: boolean
  // Shows a "Use suggested address" action for results Google standardized.
  onUseSuggested?: (address: Address) => void
}

function SuggestedAddress({
  address,
  onUseSuggested,
}: {
  address: Address
  onUseSuggested?: (address: Address) => void
}): JSX.Element {
  return (
    <>
      <p className="font-medium text-foreground">{formatAddress(address)}</p>
      {onUseSuggested && (
        <Button
          size="sm"
          variant="outline"
          className="mt-1"
          onClick={() => onUseSuggested(address)}
        >
          Use suggested address
        </Button>
      )}
    </>
  )
}

export function AddressValidationNotice({
  result,
  isChecking = false,
  isError = false,
  onUseSuggested,
}: AddressValidationNoticeProps): JSX.Element {
  if (isChecking) {
    return (
      <p
        aria-live="polite"
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Spinner className="size-4" />
        Checking address…
      </p>
    )
  }

  if (isError) {
    return (
      <Alert>
        <IconAlertTriangle />
        <AlertTitle>Address could not be verified right now</AlertTitle>
        <AlertDescription>You can still save it as entered.</AlertDescription>
      </Alert>
    )
  }

  if (!result) {
    return (
      <p className="text-sm text-muted-foreground">
        Enter a street address to verify it.
      </p>
    )
  }

  switch (result.status) {
    case 'valid':
      return (
        <p
          aria-live="polite"
          className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300"
        >
          <IconCircleCheck className="size-4" />
          {result.uspsDpvConfirmation === 'Y'
            ? 'Verified deliverable address (USPS)'
            : 'Verified address'}
        </p>
      )
    case 'corrected':
      return (
        <Alert>
          <IconWand />
          <AlertTitle>Suggested correction</AlertTitle>
          <AlertDescription>
            {result.standardized && (
              <SuggestedAddress
                address={result.standardized}
                onUseSuggested={onUseSuggested}
              />
            )}
          </AlertDescription>
        </Alert>
      )
    case 'missingUnit':
      return (
        <Alert>
          <IconAlertTriangle />
          <AlertTitle>Unit number needed</AlertTitle>
          <AlertDescription>
            This building has more than one unit. Add an apartment, suite or
            unit number in line 2.
          </AlertDescription>
        </Alert>
      )
    case 'unconfirmed': {
      const parts = describeAddressComponents(result.unconfirmedComponents)
      return (
        <Alert>
          <IconMapPinQuestion />
          <AlertTitle>Address partly confirmed</AlertTitle>
          <AlertDescription>
            {parts.length > 0
              ? `Could not confirm the ${parts.join(', ')}. Double-check before saving.`
              : 'Some parts could not be confirmed. Double-check before saving.'}
            {result.standardized && (
              <SuggestedAddress
                address={result.standardized}
                onUseSuggested={onUseSuggested}
              />
            )}
          </AlertDescription>
        </Alert>
      )
    }
    case 'invalid': {
      const missing = describeAddressComponents(result.missingComponents)
      return (
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertTitle>Address not found</AlertTitle>
          <AlertDescription>
            {missing.length > 0
              ? `Missing the ${missing.join(', ')}. Check the address and try again.`
              : 'Check the street, city and ZIP code and try again.'}
          </AlertDescription>
        </Alert>
      )
    }
  }
}
